import { FC, memo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'next-i18next';
import { DTOQualityUserBadge } from 'src/apis/dto/DTOQualityUserBadge';
import { QUALITY_USER_BADGE_STATUS_TYPES } from 'src/apis/dto/DTOQualityUserBadge';
import BadgeImage from 'src/components/QualityUserBadge/BadgeImage';
import useIsomorphicLayoutEffect from 'src/hooks/useIsomorphicLayoutEffect';
import { hexToRgb } from 'src/utils/common';
const Wrap = styled.div`
  position: relative;
  margin-top: 8px;
  padding-top: 36px;
  padding-bottom: 22px;
`;
const ProgressBar = styled.div`
  width: 100%;
  height: 3px;
  border-radius: 5px;
  background-color: ${({
  theme
}) => theme.color.surface6};
  position: relative;
`;
const ProgressBarInner = styled.div<{
  percentage: number;
}>`
  position: absolute;
  top: 0;
  ${({
  theme
}) => theme.isRtl ? 'right' : 'left'}: 0px;
  width: ${({
  percentage
}) => `${percentage}%`};
  height: 100%;
  border-radius: 5px;
  background-color: ${({
  theme
}) => theme.color.content11};
`;
const ProgressDotWrap = styled.div<{
  percentage: number;
}>`
  position: absolute;
  top: 50%;
  ${({
  theme,
  percentage
}) => (theme.isRtl ? 'right' : 'left') + `: ${percentage}%`};
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(${({
  theme
}) => hexToRgb(theme.color.surface12)}, 0.2);
  width: 15px;
  height: 15px;
  border-radius: 50%;
  transform: translate(${({
  theme
}) => theme.isRtl ? '50%' : '-50%'}, -50%);
`;
const ProgressDotInner = styled.div`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: ${({
  theme
}) => theme.color.surface12};
`;
const ProgressDescription = styled.div<{
  offset: string;
}>`
  position: absolute;
  top: 0;
  ${({
  theme,
  offset
}) => (theme.isRtl ? 'right' : 'left') + `: ${offset}`};
  padding: 6px 8px;
  border-radius: 8px;
  background-color: ${({
  theme
}) => theme.color.surface12};
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  max-width: 100%;
`;
const ProgressDescriptionIcon = styled(BadgeImage)`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;
const ProgressDescriptionText = styled.p`
  font-size: 12px;
  font-weight: 700;
  line-height: 14.32px;
  color: ${({
  theme
}) => theme.color.contentBright};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;
const Goal = styled.p`
  position: absolute;
  ${({
  theme
}) => theme.isRtl ? 'left' : 'right'}: 0px;
  bottom: 0;
  max-width: 100%;
  text-overflow: ellipsis;
  overflow: hidden;
  font-size: 12px;
  font-weight: 700;
  line-height: 14.32px;
  color: ${({
  theme
}) => theme.color.content9};
`;
interface Props {
  qualityUserBadge: DTOQualityUserBadge;
}
const QualityUserBadgeProgress: FC<Props> = ({
  qualityUserBadge
}) => {
  const descriptionRef = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState('0px');
  const {
    t
  } = useTranslation();
  useIsomorphicLayoutEffect(() => {
    if (!descriptionRef.current || !descriptionRef.current.parentElement) return;
    const descWidth = descriptionRef.current.offsetWidth;
    const containerWidth = descriptionRef.current.parentElement.offsetWidth;
    let xOffset = qualityUserBadge.percentage / 100 * containerWidth - descWidth / 2;

    // 퍼센트 기반으로 위치 결정하되 전체 boundary 벗어나지 않게 설정
    if (xOffset < 0) {
      xOffset = 0;
    } else if (xOffset + descWidth > containerWidth) {
      xOffset = containerWidth - descWidth;
    }
    setOffset(`${xOffset}px`);
  }, [qualityUserBadge.percentage]);
  const progressDescriptionText = qualityUserBadge.state === QUALITY_USER_BADGE_STATUS_TYPES.ACHIEVED ? t('badge_progress_description_complete') : `${qualityUserBadge.percentage}%`;
  return <Wrap data-sentry-element="Wrap" data-sentry-component="QualityUserBadgeProgress" data-sentry-source-file="Progress.tsx">
      <ProgressDescription ref={descriptionRef} offset={offset} data-sentry-element="ProgressDescription" data-sentry-source-file="Progress.tsx">
        <ProgressDescriptionIcon src={qualityUserBadge.badgeImageUrl} alt='Azar Badge Current Progress' showFallback={true} data-sentry-element="ProgressDescriptionIcon" data-sentry-source-file="Progress.tsx" />
        <ProgressDescriptionText data-sentry-element="ProgressDescriptionText" data-sentry-source-file="Progress.tsx">{progressDescriptionText}</ProgressDescriptionText>
      </ProgressDescription>
      <ProgressBar data-sentry-element="ProgressBar" data-sentry-source-file="Progress.tsx">
        <ProgressDotWrap percentage={qualityUserBadge.percentage} data-sentry-element="ProgressDotWrap" data-sentry-source-file="Progress.tsx">
          <ProgressDotInner data-sentry-element="ProgressDotInner" data-sentry-source-file="Progress.tsx" />
        </ProgressDotWrap>
        <ProgressBarInner percentage={qualityUserBadge.percentage} data-sentry-element="ProgressBarInner" data-sentry-source-file="Progress.tsx" />
      </ProgressBar>
      <Goal data-sentry-element="Goal" data-sentry-source-file="Progress.tsx">{t('badge_progress_goal')}</Goal>
    </Wrap>;
};
export default memo(QualityUserBadgeProgress);